import { useTools } from '@/composables/useTools'
import { useTranslation } from '@/composables/useTranslation'
import { TaxonomicObjectAlertInput } from '@/models/Alerts'
import { BarcodeOption, BarcodeType, RenderFormat } from '@/models/Ean'
import { ProductExportTemplate, ProductExportTemplateInput } from '@/models/Export'
import { Filter } from '@/models/Filter'
import { StringObject } from '@/models/GenericInterfaces'
import { Country, Culture, CultureOverrides, Direction, LinkGroup, PimLocationQuery } from '@/models/Global'
import { PriceAdjustmentMethod, ProductImportTemplateInput, ProductInput, StoredEditingSession, TaxonomicObjectInput } from '@/models/Import'
import Message from '@/models/Message'
import { AvailableEanLabelData, CountrySpecifics, ProductImages, ProductsResponse, SimplifiedProduct, UnavailableAssetData } from '@/models/Product'
import {
  AdvancedProperty,
  InstructionType,
  PropertyDeadlineLevel,
  PropertyDeadlines,
  PropertyMapping,
  PropertyMappingType,
  PropertyTemplateInput,
  StoredProperty,
} from '@/models/Property'
import { DataSearchResult } from '@/models/Search'
import { Settings } from '@/models/Settings'
import { RegisteredTaxonomy, SimplifiedCategory, SuperBaseTaxonomicObject } from '@/models/Taxonomy'
import { FieldMapping, TemplateType, TemplateVisibility } from '@/models/Templates'
import { StagedTranslationDocument, TranslationDocument, TranslationMoveInput } from '@/models/Translation'
import { AuthRole, User, UserPrivilege } from '@/models/User'
import { useDefaultStore } from '@/store'
import { usePropertyStore } from '@/store/propertyStore'
import { arrayToRecord, clone, sanitizeObjectProperties } from '@/utils/tools'
import { ComputeInstructions } from '@burde/pim-shared'
import axios, { AxiosRequestConfig } from 'axios'
import { FirebaseOptions, getApp } from 'firebase/app'
import {
  collection,
  doc,
  DocumentData,
  DocumentReference,
  DocumentSnapshot,
  getFirestore,
  onSnapshot,
  Query,
  QuerySnapshot,
  setDoc,
  Timestamp,
} from 'firebase/firestore'
import ApiService from './ApiService'

class DataService {
  private get store() {
    return useDefaultStore()
  }

  private get propertyStore() {
    return usePropertyStore()
  }

  private getTranslation() {
    return useTranslation()
  }

  private get appTranslate() {
    return this.getTranslation().appTranslate
  }

  private get addDefaultErrorMsg() {
    return useTools().addDefaultErrorMsg
  }

  private get db() {
    const app = getApp()
    return getFirestore(app)
  }

  get domain() {
    return this.db.app.options?.authDomain
  }

  // region Helper functions

  private activeObservers = new Map<
    string,
    {
      snapshot: DocumentSnapshot<DocumentData, DocumentData> | QuerySnapshot<DocumentData, DocumentData> | null
      promise: Promise<unknown>
      unsubscribe?: () => void
    }
  >()

  private observeFirestoreCollection<T>(path: string, callback: (snapshot: QuerySnapshot<DocumentData>) => T | Promise<T>, unsubOnSignout = true): Promise<T> {
    return this.observeFirestore(path, this.getCollectionRef(path), callback, unsubOnSignout)
  }

  // identical to observeFirestoreCollection but for the snapshot type
  private observeFirestoreDoc<T>(
    path: string,
    callback: (snapshot: DocumentSnapshot<DocumentData, DocumentData>) => T | Promise<T>,
    unsubOnSignout = true,
  ): Promise<T> {
    return this.observeFirestore(path, this.getDocRef(path), callback, unsubOnSignout)
  }

  private isDocumentReference(
    ref: DocumentReference<DocumentData, DocumentData> | Query<DocumentData, DocumentData>,
  ): ref is DocumentReference<DocumentData, DocumentData> {
    return ref.type === 'document'
  }

  private observeFirestore<T>(
    path: string,
    ref: DocumentReference<DocumentData, DocumentData>,
    callback: (snapshot: DocumentSnapshot<DocumentData, DocumentData>) => T | Promise<T>,
    unsubOnSignout?: boolean,
  ): Promise<T>
  private observeFirestore<T>(
    path: string,
    ref: Query<DocumentData, DocumentData>,
    callback: (snapshot: QuerySnapshot<DocumentData>) => T | Promise<T>,
    unsubOnSignout?: boolean,
  ): Promise<T>
  private observeFirestore<T>(
    path: string,
    ref: DocumentReference<DocumentData, DocumentData> | Query<DocumentData, DocumentData>,
    callback: ((snapshot: DocumentSnapshot<DocumentData, DocumentData>) => T | Promise<T>) | ((snapshot: QuerySnapshot<DocumentData>) => T | Promise<T>),
    unsubOnSignout = true,
  ): Promise<T> {
    if (!this.activeObservers.has(path)) {
      let resolveFn!: (value: T) => void
      let rejectFn!: (err: Error) => void

      const dataPromise = new Promise<T>((resolve, reject) => {
        resolveFn = resolve
        rejectFn = reject
      })

      const unsubscribe = this.isDocumentReference(ref)
        ? onSnapshot(
            ref,
            (snapshot) => {
              const observer = this.activeObservers.get(path)
              if (observer) {
                observer.snapshot = snapshot
                const docCallback = callback as (snap: DocumentSnapshot<DocumentData, DocumentData>) => T | Promise<T>
                Promise.resolve(docCallback(snapshot)).then(resolveFn).catch(rejectFn)
              }
            },
            (error) => {
              console.error(`Firestore snapshot error on path "${path}":`, error)
            },
          )
        : onSnapshot(
            ref,
            (snapshot) => {
              const observer = this.activeObservers.get(path)
              if (observer) {
                observer.snapshot = snapshot
                const queryCallback = callback as (snap: QuerySnapshot<DocumentData>) => T | Promise<T>
                Promise.resolve(queryCallback(snapshot)).then(resolveFn).catch(rejectFn)
              }
            },
            (error) => {
              console.error(`Firestore snapshot error on path "${path}":`, error)
            },
          )

      this.activeObservers.set(path, {
        snapshot: null,
        promise: dataPromise,
        unsubscribe: unsubOnSignout ? unsubscribe : undefined,
      })
    }

    return this.activeObservers.get(path)!.promise as Promise<T>
  }

  private unObserveFirestore(path: string) {
    const observer = this.activeObservers.get(path)
    if (observer) {
      observer.unsubscribe?.()
      this.activeObservers.delete(path)
    }
  }

  private getCollectionRef(collectionPath: string) {
    return collection(this.db, collectionPath)
  }

  private getDocRef(docPath: string) {
    return doc(this.db, docPath)
  }

  getCultureOverrides(culture?: Culture): CultureOverrides | undefined {
    if (culture) {
      return {
        translationLanguage: culture.language,
        dataLanguage: culture.language,
        dataCountry: culture.country,
        includeAllCultures: false,
      }
    }
    return undefined
  }

  // region App load functions. Firebase listeners

  unsubAllFirebaseObservers() {
    for (const observer of this.activeObservers.values()) {
      observer.unsubscribe?.()
    }
    this.activeObservers.clear()
  }

  /**
   * Listens to the user doc belonging to the logged in user
   * Updates and stores user auth levels accordingly when new data is received
   */
  async loadUserData(email: string) {
    return this.observeFirestoreDoc(`users/${email}`, async () => {
      // try {
      this.store.currentUser = await ApiService.get<User | null>('users/me')
      // } catch (err) {
      //   console.log('ERROR LOADING USER DATA')
      // throw new Error(err)
      // }
      this.store.useNumericValuesForExport = !!this.store.currentUser?.useNumericValuesForExportByDefault // set default value
      return this.store.currentUser
    })
  }

  /**
   * Attaches a listener to the settings collections and stores the result in store.settings
   */
  async loadSettings() {
    return this.observeFirestoreCollection<Settings>(
      'settings',
      (collection) =>
        (this.store.settings = collection.docs.reduce((acc: any, doc) => {
          acc[doc.id] = doc.data()
          return acc
        }, {})),
      false,
    )
  }

  /**
   * Attaches a listener to the translations document and stores the result in store.translations
   */
  async loadTranslations() {
    return this.observeFirestoreDoc<TranslationDocument | undefined>(
      'translations/v3',
      (doc) => {
        this.store.translations = doc.data()
        return this.store.translations
      },
      false,
    )
  }

  async saveTranslations(translationChanges: StagedTranslationDocument) {
    await setDoc(this.getDocRef('translations/v3'), translationChanges, { merge: true })
  }

  getParams(query?: PimLocationQuery, cultureOverrides?: CultureOverrides | null) {
    const params = new URLSearchParams(query as Record<string, string>)

    if (cultureOverrides !== null) {
      if (cultureOverrides?.dataLanguage !== null) {
        params.set('dataLanguage', cultureOverrides?.dataLanguage || this.store.dataCulture?.language || '')
      }
      if (cultureOverrides?.dataCountry !== null) {
        params.set('dataCountry', cultureOverrides?.dataCountry || this.store.dataCulture?.country || '')
      }

      if (cultureOverrides?.translationLanguage !== null) {
        params.set('translationLanguage', cultureOverrides?.translationLanguage || this.store.translationLanguage)
      }

      if (cultureOverrides?.includeCultureExclusiveData) {
        params.set('includeCultureExclusiveData', '1')
      }

      if (cultureOverrides?.includeAllCultures ?? this.store.overrideIncludeAllCultures) {
        params.set('includeAllCultures', '1')
        // params.set('includedCountries', this.store.availableCountries.join(','))
        // params.set('includeProductsMissingAvailableCountries', '1')
      }

      const includedCountries = cultureOverrides?.includedCountries ?? Array.from(this.store.includedCountries)
      params.set('includedCountries', includedCountries.join(','))

      if (cultureOverrides?.includeProductsMissingAvailableCountries || this.store.includeProductsMissingAvailableCountries) {
        params.set('includeProductsMissingAvailableCountries', '1')
      }
    }

    if (this.store.productType) {
      params.set('productType', this.store.productType)
    }

    return params
  }

  async getInitiationSettings() {
    return ApiService.get<{ name: string; config: FirebaseOptions }>('init', undefined, undefined, true)
  }

  // region Filter functions --

  // used by ActiveFiltersComponent
  async getAvailableFilters(query?: PimLocationQuery) {
    return ApiService.get<Filter[]>(this.getFilterMatchUrl(query, false), undefined, 'v2')
  }

  // public since filter component uses it to compare against current query
  getFilterMatchUrl(query?: PimLocationQuery, includeMatches = true): string {
    const queryParams = this.getParams(query)
    includeMatches ? queryParams.set('includeMatches', '1') : 0
    return `filters?${queryParams.toString()}`
  }

  async getFiltersWithMatches(query?: PimLocationQuery): Promise<{ filters: Filter[]; query: string } | undefined> {
    try {
      const queryString = this.getFilterMatchUrl(query)
      const filters = await ApiService.get<Filter[]>(queryString, undefined, 'v2')
      return { filters, query: queryString }
    } catch (err: any) {
      this.store.addMessage(new Message('Kunde inte hämta filtermatchningar', err, 'danger'))
    }
  }

  //region Product functions

  async getProducts<T = ProductsResponse>(
    query?: PimLocationQuery,
    cultureOverrides?: CultureOverrides | null,
    params?: {
      doNotUseStandardOptionsAsFallback?: boolean
    },
    config?: AxiosRequestConfig,
  ) {
    try {
      const queryParams = this.getParams(query, cultureOverrides)
      if (params?.doNotUseStandardOptionsAsFallback !== undefined) {
        queryParams.set('doNotUseStandardOptionsAsFallback', params.doNotUseStandardOptionsAsFallback ? '1' : '0')
      }
      return await ApiService.get<T>(`products?${queryParams.toString()}`, config, 'v2')
    } catch (err: any) {
      this.store.addMessage(new Message('Kunde inte hämta artiklar', err, 'danger'))
      return { total: 0, results: 0, data: [], errored: true } as T
    }
  }

  async getProductsSkus(query?: PimLocationQuery, cultureOverrides?: CultureOverrides | null, config?: AxiosRequestConfig) {
    return this.getProducts<ProductsResponse<string>>({ ...query, onlySkus: 1 }, cultureOverrides, undefined, config)
  }

  async getProductsBySku(skus: string[]) {
    return this.getProducts({ _field_sku: skus }, { includeAllCultures: true })
  }

  async getProductTotalsByCountry(query: PimLocationQuery) {
    return ApiService.get<Partial<Record<Country | 'missing', number>>>(`products/totals-by-country?${this.getParams(query).toString()}`, undefined, 'v2')
  }

  async findProductData(
    searchString: string,
    query: PimLocationQuery,
    startingRow?: number,
    startingColumn?: number,
    direction?: Direction,
    shownProperties?: AdvancedProperty[],
    includedProperties?: AdvancedProperty[],
    includeAllResults?: boolean,
    isRegexSearch?: boolean,
    matchCase?: boolean,
    exactMatch?: boolean,
    productInputBySku?: { [sku: string]: ProductInput },
    extraProducts?: SimplifiedProduct[],
    newProducts?: SimplifiedProduct[],
    expiredProducts?: SimplifiedProduct[],
  ) {
    try {
      const queryParams = this.getParams(query)

      return await ApiService.post<DataSearchResult | undefined>(
        `products/find-data?${queryParams.toString()}`,
        {
          searchString,
          startingRow,
          startingColumn,
          direction,
          dataLanguage: this.store.dataCulture?.language,
          isRegexSearch,
          shownProperties,
          includedProperties,
          includeAllResults,
          matchCase,
          exactMatch,
          editedProductsBySku: productInputBySku,
          extraProducts,
          newProducts,
          expiredProducts,
        },
        undefined,
        'v2',
      )
    } catch (err: any) {
      this.addDefaultErrorMsg(err)
    }
  }

  async getIndicesOfSkus(
    skus: string[],
    startingRow = 0,
    direction = Direction.forwards,
    query: PimLocationQuery,
  ): Promise<Array<{ index: number; sku: string }> | undefined> {
    try {
      const queryParams = this.getParams(query)

      return await ApiService.post(
        `products/get-indices?${queryParams.toString()}`,
        {
          skus,
          startingRow,
          direction,
        },
        undefined,
        'v2',
      )
    } catch (err: any) {
      this.addDefaultErrorMsg(err)
    }
  }

  /**
   *
   * @param sku
   * @param checkExistance return empty result with no 404
   * @returns
   */
  async getProduct(
    sku: number | string,
    checkExistance?: boolean,
    cultureOverrides?: CultureOverrides,
    /** still relevant due to images */
    forceRecache?: boolean,
    config?: AxiosRequestConfig,
    extraQuery?: PimLocationQuery,
  ) {
    try {
      const queryParams = this.getParams(extraQuery, cultureOverrides)
      checkExistance && queryParams.set('checkExistance', '1')
      forceRecache && queryParams.set('recache', '1')

      return await ApiService.get<SimplifiedProduct | undefined>(`products/${sku}?${queryParams.toString()}`, config, 'v2')
    } catch (err: any) {
      this.store.addMessage(new Message('Kunde inte hämta artikel', err, 'danger'))
    }
  }

  async getProductVersion(sku: number | string, version: string, checkExistance?: boolean, cultureOverrides?: CultureOverrides, config?: AxiosRequestConfig) {
    try {
      const queryParams = this.getParams(undefined, cultureOverrides)
      checkExistance && queryParams.set('checkExistance', '1')

      return await ApiService.get<ProductInput>(`products/${sku}/version/${version}?${queryParams.toString()}`, config, 'v2')
    } catch (err: any) {
      this.store.addMessage(new Message('Kunde inte hämta artikel', err, 'danger'))
    }
  }

  async getEanLabelData(product?: SimplifiedProduct) {
    try {
      return await ApiService.post<AvailableEanLabelData | UnavailableAssetData>('products/ean-labels/data', { product }, undefined, 'v2')
    } catch (err) {
      this.store.addMessage(new Message('Kunde inte hämta EAN-data', err, 'danger'))
    }
  }

  /**
   * @param sku product SKU
   * @param deletePermanently
   * @returns true if successful, false otherwise
   */
  async deleteProduct(sku: string, deletePermanently = false) {
    try {
      const queryParams = new URLSearchParams()
      deletePermanently ? queryParams.set('deletePermanently', '1') : undefined
      return ApiService.delete<boolean>(`products/${sku}?${queryParams.toString()}`, undefined, 'v2')
    } catch (err) {
      this.store.addMessage(new Message(this.appTranslate('An error occured'), 'Ett fel uppstod vid borttagning av artikeln', 'danger'))
      return false
    }
  }

  /**
   * @param deletePermanently
   * @returns true if successful, false otherwise
   */
  async deleteProducts(skus: string[], deletePermanently = false) {
    try {
      const queryParams = this.getParams()
      // queryParams.set('_field_sku_equals', skus.join(','))
      queryParams.set('skus', skus.join(','))
      deletePermanently ? queryParams.set('deletePermanently', '1') : undefined
      return ApiService.delete<boolean>(`products?${queryParams.toString()}`, undefined, 'v2')
    } catch (err) {
      this.store.addMessage(new Message(this.appTranslate('An error occured'), 'Ett fel uppstod vid borttagning av artikeln', 'danger'))
      return false
    }
  }

  async search(
    searchString: string,
    productsFrom = 0,
    productsLimit = 5,
    includeCategories: boolean,
    includeVersions?: boolean,
    cultureOverrides?: CultureOverrides,
  ) {
    try {
      const productsQueryParams = this.getParams(
        { from: productsFrom, to: productsFrom + productsLimit, ...(includeVersions && { includeVersions: 1 }) },
        cultureOverrides,
      )
      const productsResponse = ApiService.get<ProductsResponse>(`products?_q=${searchString}&${productsQueryParams.toString()}`, undefined, 'v2')

      let categoriesResponse
      if (includeCategories) {
        const categoriesQueryParams = this.getParams(undefined, cultureOverrides)
        categoriesResponse = ApiService.get<{ total: number; results: number; data: Array<SimplifiedCategory & { parentName?: string }> }>(
          `taxonomies/categories/find?q=${searchString}&${categoriesQueryParams.toString()}`,
          undefined,
          'v2',
        )
      }

      return { searchString, products: await productsResponse, categories: await categoriesResponse }
    } catch (err: any) {
      this.store.addMessage(new Message('Kunde inte hämta sökresultat', err, 'danger'))
    }
  }

  public async getPriceAdjustments(
    skus: string[],
    productInputBySku: { [sku: string]: ProductInput },
    adjustmentAmount: number,
    method: PriceAdjustmentMethod,
    selectedCountries: Country[],
    sourceField: keyof CountrySpecifics,
    targetField: keyof CountrySpecifics,
  ) {
    return ApiService.post<{
      productInputBySku: { [sku: string]: ProductInput }
      unchanged: { missingSourcePrice: { [sku: string]: Country[] }; sameAsCurrentPrice: { [sku: string]: Country[] }; errored: { [sku: string]: string[] } }
    }>(
      'products/adjust-prices',
      {
        skus,
        productInputBySku,
        adjustment: { ...(method !== 'remove' && { amount: adjustmentAmount }), method },
        selectedCountries,
        sourceField,
        targetField,
      },
      undefined,
      'v2',
    )
  }

  async clearFilterMatchCache() {
    return ApiService.get('filters/clear-match-cache', undefined, 'v2')
  }

  // async getEan(sku: string, eanType: EanType, eanAlgorithm?: Ean13Algorithm): Promise<{ ean: number }> {
  //   const params = new URLSearchParams({ eanType })
  //   if (eanAlgorithm) {
  //     params.set('eanAlgorithm', eanAlgorithm)
  //   }
  //   return ApiService.get(`products/ean13/${sku}?${params.toString()}`, undefined, 'v2')
  // }

  async generateEanCodes(
    productPreviews: Array<ProductInput | SimplifiedProduct>,
    codeTypes: BarcodeType[],
    eanAlgorithm?: string,
  ): Promise<{
    codes: { [sku: string]: Partial<Record<BarcodeType, number | string>> }
    failures: { categorization: []; generation: Array<{ codeType: BarcodeType; sku: string }> }
  }> {
    return ApiService.post('products/ean13', { productPreviews, codeTypes, eanAlgorithm }, undefined, 'v2')
  }

  async getBarcodeCollisions(productPreviews: ProductInput[]): Promise<{ collisionsWithExisting: number[]; duplicatesInEdit: number[] }> {
    return ApiService.post(`products/barcode-collisions`, { productPreviews }, undefined, 'v2')
  }

  private barcodeOptionsLoading = false
  async loadAvailableBarcodeOptions(refresh?: boolean) {
    try {
      if (!this.barcodeOptionsLoading && (!this.store.availableBarcodeOptions || refresh)) {
        this.barcodeOptionsLoading = true
        const result = await ApiService.get<BarcodeOption[]>('products/ean13-algorithms', undefined, 'v2')
        this.store.availableBarcodeOptions = result?.sort((a, b) => a.title.localeCompare(b.title))
      }
      return this.store.availableBarcodeOptions
    } catch (err) {
      this.addDefaultErrorMsg(err)
    } finally {
      this.barcodeOptionsLoading = false
    }
  }

  // region Taxonomic functions --

  async addTaxonomicObject(taxonomy: RegisteredTaxonomy, taxonomicObject: TaxonomicObjectInput, parentId?: string, checkForDuplicate?: boolean) {
    const endpoint = `taxonomies/${taxonomy}/${parentId || ''}?checkForDuplicate=${checkForDuplicate}`
    try {
      const response = await ApiService.post<{ obj: SuperBaseTaxonomicObject }>(endpoint, taxonomicObject, undefined, 'v2')
      return response.obj
    } catch (err: any) {
      throw new Error(err.response?.data || err)
    }
  }

  async deleteTaxonomicObject(taxonomy: RegisteredTaxonomy, taxonomicObjectId: string, parentId?: string) {
    try {
      const path = taxonomy === 'attributes' && parentId ? `${parentId}/${taxonomicObjectId}` : taxonomicObjectId
      await ApiService.delete(`taxonomies/${taxonomy}/${path}`, undefined, 'v2')
    } catch (err) {
      console.log(err)
    }
  }

  async commitTaxonomicObject(taxonomy: RegisteredTaxonomy, objectId: string, input: TaxonomicObjectInput, termParentId?: string) {
    const url = ['taxonomies', taxonomy, objectId, termParentId].join('/')
    const result = await ApiService.patch(url, input, undefined, 'v2')
    if (input.id) {
      this.removeAlerts('taxonomies', [{ taxonomy, id: objectId }] as TaxonomicObjectAlertInput[])
    }
    return result
  }

  /** Merges two taxonomid objects and adjusts product references  */
  async mergeTaxonomyObjects(sourceId: string, targetId: string, taxonomy: RegisteredTaxonomy, termParentId?: string) {
    return ApiService.put(`taxonomies/${taxonomy}/merge/${termParentId || ''}`, { sourceId, targetId }, undefined, 'v2')
  }

  async moveTranslation(translationMoveInput: TranslationMoveInput) {
    return ApiService.post(`translations/move`, translationMoveInput)
  }

  async loadAlerts(document: 'taxonomies') {
    return this.observeFirestoreDoc<TaxonomicObjectAlertInput>(`alerts/${document}`, (doc) => (this.store.taxonomicObjectAlerts = doc.get('objects')))
  }

  async removeAlerts(document: 'taxonomies', alerts: TaxonomicObjectAlertInput[]) {
    const indexedAlerts = arrayToRecord(alerts, 'id')

    const objects = this.store.taxonomicObjectAlerts
      ?.filter((entry) => {
        const alert = indexedAlerts?.[entry.id]
        return !alert || entry.taxonomy !== alert.taxonomy
      })
      .map((alert) => {
        // fix firebase timestamp
        const date = alert.date && '_seconds' in alert.date ? Timestamp.fromDate(new Date((alert.date as any)._seconds * 1000)) : alert.date
        return { ...alert, date }
      })
    const docRef = this.getDocRef(`alerts/${document}`)
    return setDoc(docRef, { objects }, { merge: true })
  }

  async clearAlerts(document: 'taxonomies') {
    const docRef = this.getDocRef(`alerts/${document}`)
    return setDoc(docRef, { objects: [] }, { merge: true })
  }

  //region Import/export template

  /**
   *
   * @param type
   * @param visibility
   * @param refreshIds forces immediate refresh for these ids to avoid sync issues when fetching from backend, but its trigged by frontend listener
   */
  private async updateTemplates(type: TemplateType, visibility: TemplateVisibility, refreshIds?: string[]) {
    const params = new URLSearchParams({})

    if (refreshIds) {
      params.set('refreshIds', refreshIds?.join(','))
    }

    const result = await ApiService.get<{
      collectionPath: string
      templates: { name: string; id: string; mappings: FieldMapping[] }[]
    }>(`templates/${type}/${visibility}?${params.toString()}`)

    this.store.templates[type][visibility] = result.templates
    return result
  }

  async loadTemplates(type: TemplateType, visibility: TemplateVisibility) {
    try {
      if (this.store.templates[type][visibility] === undefined) {
        this.store.templates[type][visibility] = null

        let initiation = true

        const result = await this.updateTemplates(type, visibility)
        this.observeFirestoreCollection(result.collectionPath, (collection) => {
          if (initiation) {
            // skip first
            initiation = false
            return
          }

          const updated = collection.docChanges().map((change) => change.doc.id)
          this.updateTemplates(type, visibility, updated)
        })
      }
    } catch (err) {
      console.log(err)
      this.store.addMessage(new Message(this.appTranslate(`Failed to fetch %s %s templates`, [visibility, type]), err, 'danger'))
    }
  }

  async loadPropertyDeadlines() {
    return this.observeFirestoreDoc('templates/property-deadlines', (doc) => {
      this.propertyStore.deadlines = doc.data() as PropertyDeadlines
      return this.propertyStore.deadlines
    })
  }

  async saveTemplate(
    type: 'export',
    template: ProductExportTemplateInput,
    visibility: TemplateVisibility,
    existingId?: string,
    merge?: boolean,
  ): Promise<ProductImportTemplateInput & { id: string }>
  async saveTemplate(
    type: 'import',
    template: ProductImportTemplateInput,
    visibility: TemplateVisibility,
    existingId?: string,
    merge?: boolean,
  ): Promise<ProductImportTemplateInput & { id: string }>
  async saveTemplate(
    type: 'properties',
    template: PropertyTemplateInput,
    visibility: TemplateVisibility,
    existingId?: string,
    merge?: boolean,
  ): Promise<PropertyTemplateInput & { id: string }>
  async saveTemplate<T extends ProductExportTemplate | ProductImportTemplateInput | PropertyTemplateInput>(
    type: TemplateType,
    template: T,
    visibility: TemplateVisibility,
    existingId?: string,
    merge = false,
  ) {
    try {
      const sanitizedTemplate = clone(template)
      sanitizeObjectProperties(sanitizedTemplate)

      const response = await ApiService.post<T>(`templates/${type}/${visibility}/${existingId || ''}`, { input: sanitizedTemplate, merge })
      const typeName = this.appTranslate(`the ${type}-template`)
      this.store.addMessage(new Message(this.appTranslate(`%s "%s" was saved`, [typeName, template.name]), undefined, undefined, 5))

      return response
    } catch (err) {
      console.log(err)
      this.store.addMessage(new Message(this.appTranslate(`The template could not be saved`), err, 'danger'))
    }
  }

  async removeTemplate(type: TemplateType, id: string, visibility: TemplateVisibility) {
    try {
      await ApiService.delete(`templates/${type}/${visibility}/${id}`)
      const typeName = this.appTranslate(`the ${type}-template`)
      this.store.addMessage(new Message(this.appTranslate(`%s was removed`, [typeName]), undefined, undefined, 5))
    } catch (err) {
      console.log(err)
      this.store.addMessage(new Message(`The template could not be removed`, err, 'danger'))
    }
  }

  async savePropertyDeadlines(input: PropertyDeadlineLevel) {
    try {
      await ApiService.post(`templates/property-deadlines`, { input })
    } catch (err) {
      console.log(err)
      this.store.addMessage(new Message(`Deadline level could not be saved`, err, 'danger'))
    }
  }

  /**
   * Images are normally included in the product backend response
   * This is used when creating a new product (and from presentations still)
   */
  async getAvailableImgsForProduct(sku: string) {
    return ApiService.get<ProductImages>(`products/images/${sku}`, undefined, 'v2')
  }

  async listImages(search: string): Promise<string[]> {
    const baseUrl = 'https://imgs.burde.com/api/listAvailableImages'
    const response = await axios.get(`${baseUrl}/${search}`)
    return response.data.images
  }

  //region Auth and logs

  private authRolesLoading = false
  async loadAuthRoles(refresh?: boolean): Promise<AuthRole[] | undefined> {
    try {
      if (!this.authRolesLoading && (!this.store.userRoles || refresh)) {
        this.authRolesLoading = true
        const roles: AuthRole[] = await ApiService.get('auth/roles')
        roles.forEach((role) => role.privileges?.sort((a, b) => a.localeCompare(b)))
        this.store.userRoles = roles
      }
      return this.store.userRoles
    } catch (err) {
      console.log(err)
    } finally {
      this.authRolesLoading = false
    }
  }

  async getAvailableAuthPrivileges() {
    return ApiService.get<{ name: UserPrivilege; description: string }[]>('auth/privileges')
  }

  async getLogFolders() {
    return ApiService.get<string[]>('logs')
  }

  async getLogs(folder: string) {
    return ApiService.get<string[]>(`logs/${folder}`)
  }

  async getLog(folder: string, fileName: string) {
    return ApiService.get<string>(`logs/${folder}/${fileName}`)
  }

  async downloadLog(folder: string, fileName: string) {
    return ApiService.download(`logs/${folder}/${fileName}`, fileName.replace(/.gz$/, ''))
  }

  async searchLogs(searchString: string, folder: string, from?: string, to?: string) {
    const params = new URLSearchParams({ searchString })
    from && params.set('from', from)
    to && params.set('to', to)
    return ApiService.post<string[]>(`logs/${folder}/search?${params.toString()}`)
  }

  async *searchLogs_stream(
    searchString: string,
    regexSettings: { flags: string } | null,
    folder: string,
    from?: string,
    to?: string,
    order?: 'asc' | 'desc',
    signal?: AbortSignal,
  ) {
    const params = new URLSearchParams({ searchString })
    from && params.set('from', from)
    to && params.set('to', to)
    order && params.set('order', order)
    if (regexSettings) {
      params.set('isRegexSearch', '1')
      params.set('regexFlags', regexSettings.flags)
    }

    const response = ApiService.stream<{ processed: number; total: number; file?: string }>(`logs/${folder}/search/stream`, {
      query: params,
      signal,
      method: 'POST',
    })

    yield* response
  }

  // region Properties
  async loadProperties() {
    const version = this.store.localEnvironment
    return this.observeFirestoreDoc(`properties/${version}`, async (doc) => {
      this.loadPropertyDeadlines().catch((err) => console.log('error loading property deadlines', err))
      const instructions = this.loadPropertyInstructions(true).catch((err) => console.log('error refreshing instructions', err)) // refresh instructions
      const data: Array<{ props: StoredProperty[] }> = await doc.get('propertyGroups')
      this.propertyStore.storedProperties = data.map((propertyGroup) => propertyGroup.props)
      await instructions
      return this.propertyStore.storedProperties
    })
  }

  private instructionsLoading = false
  public async loadPropertyInstructions(refresh?: boolean) {
    type IndexedPropertyInstructions = Partial<Record<string, ComputeInstructions[]>>

    try {
      if (!this.instructionsLoading && (!this.propertyStore.propertyInstructions || refresh)) {
        this.instructionsLoading = true
        const result = await ApiService.get<{
          instructions: Record<InstructionType, IndexedPropertyInstructions>
          mappings: Record<PropertyMappingType, PropertyMapping[]>
        }>(`products/property-instructions`, undefined, 'v2')
        this.propertyStore.propertyInstructions = result.instructions
        this.propertyStore.propertyMappings = result.mappings
      }
      return { instructions: this.propertyStore.propertyInstructions, mappings: this.propertyStore.propertyMappings }
    } catch (err) {
      this.addDefaultErrorMsg(err)
      console.log(err)
    } finally {
      this.instructionsLoading = false
    }
  }

  // region Editing sessions

  async getNewSessionId() {
    return ApiService.get<string>(`editing-sessions/new/id`)
  }

  async checkOutSession(id: string | number): Promise<StoredEditingSession | undefined> {
    const session = await ApiService.put<StoredEditingSession>(`editing-sessions/checkout/${id}`)
    return session ? this.getFixedSessionFormat(session) : undefined
  }

  async checkInSession(id: string | number) {
    return ApiService.put<StoredEditingSession | undefined>(`editing-sessions/checkin/${id}`)
  }

  async getUserEditingSessions(discarded?: boolean) {
    const params = new URLSearchParams(discarded ? { discarded: '1' } : undefined)
    const result = await ApiService.get<StoredEditingSession[]>(`editing-sessions?${params.toString()}`)
    return result.map((session) => this.getFixedSessionFormat(session))
  }

  private getFixedSessionFormat(session: StoredEditingSession) {
    if (!session.includedCountries) {
      return {
        ...session,
        includedCountries: session.includeAllProductsCultures ? this.store.availableCountries : session.dataCulture ? [session.dataCulture.country] : [],
      }
    }
    return session
  }

  async saveUserEditingSession(session: StoredEditingSession, id: number | string) {
    return ApiService.post(`editing-sessions/${id}`, session)
    // return ApiService.sendBeacon(`editing-sessions/${id}`, session) // doesn't work with large sessions
  }

  /**
   * @param id
   * @param discarded delete from bin
   * @param overrideDeletePermanently always delete permanently
   * @param silent does not give 404 if not found
   * @returns
   */
  async removeEditingSession(id: number | string, discarded?: boolean, overrideDeletePermanently?: boolean, silent?: boolean) {
    const params = new URLSearchParams()
    discarded ? params.set('discarded', '1') : undefined
    silent ? params.set('silent', '1') : undefined
    overrideDeletePermanently ? params.set('overrideDeletePermanently', '1') : undefined
    return await ApiService.delete(`editing-sessions/${id}?${params.toString()}`)
  }

  async restoreDiscardedUserEditingSession(id: string | number) {
    return ApiService.put(`editing-sessions/restore/${id}`)
  }

  // region User

  async getSavedUserTableColumnSizes() {
    try {
      return await ApiService.get<StringObject>('users/table-column-sizes')
    } catch (err) {
      console.log(err)
    }
  }

  async saveUserTableColumnSizes(data: StringObject) {
    try {
      return ApiService.post('users/table-column-sizes', data)
    } catch (err) {
      console.log(err)
    }
  }

  // region  Misc
  async loadLinks(document: 'productsArchiveMenu') {
    return this.observeFirestoreDoc(`links/${document}`, (doc) => {
      this.store.links[document] = doc.get('groups') as LinkGroup[]
      return this.store.links[document]
    })
  }

  async downloadFlap(sku: string, renderFormat: RenderFormat) {
    const query = new URLSearchParams({ renderFormat })
    const response = await ApiService.download(`products/${sku}/flap?${query.toString()}`, `${sku}-flap.${renderFormat}`, 'v2')
    if (response.status !== 200) {
      this.store.addMessage(new Message('Kunde inte hämta flärp', `Status ${response.status}: ${response.statusText}`, 'danger'))
    }
  }

  async triggerJeevesUpdate(skus: string[]) {
    type SyncTargetResult = { name: string; success: boolean; err?: string }
    type Response = { msg?: string | undefined; err?: string | Partial<Record<string, unknown>> | undefined; targets?: SyncTargetResult[] }

    try {
      const response = await ApiService.post<Response>('products-sync/jeeves', { skus })

      if (!response) {
        this.store.addMessage(new Message('Fick inget svar från Jeeves', undefined, 'danger'))
      }
      return response
    } catch (err) {
      this.store.addMessage(new Message(this.appTranslate('An error occured'), `Kunde inte synka ${skus.join(', ')} till Jeeves`, 'danger'))
    }
  }

  // region Recache functions (mainly for dev)

  async clearSimplifiedProductsCache() {
    return ApiService.get('products/clear-simplified-cache', undefined, 'v2')
  }

  async clearProductImgCache() {
    return ApiService.get('products/clear-img-cache', undefined, 'v2')
  }
}
export default new DataService()
